import { useState } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'

import Box from '@mui/material/Box'

import Sidebar from './Sidebar'
import TopBar from './TopBar'
import ToastStack from './ToastStack'
import { useAuth } from '../auth/AuthContext'
import { useLiveUpdates } from '../hooks/useLiveUpdates'

// AppShell wraps every authenticated route with the sidebar + top bar
// pattern used by the sibling Insight Delivered products (FinanceLens,
// BDXLens). Pages render into <Outlet /> and no longer carry their own
// AppBar/Toolbar.
//
// Cross-cutting concerns hoisted here so they run once per session, not
// once per page mount:
//   - `useLiveUpdates` opens ONE WebSocket for the whole app
//   - <ToastStack> surfaces critical exception toasts on every page
//   - The top bar's live-data chip is fed by the same hook
//   - Platform-active users are bounced away from tenant surfaces (a
//     platform_admin has no tenant context so tenant queries would return
//     empty; they must switch workspace via the profile menu to visit them)
export default function AppShell() {
  const { status: wsStatus, lastMessage } = useLiveUpdates()
  const { user } = useAuth()
  const location = useLocation()
  const [mobileNavOpen, setMobileNavOpen] = useState(false)

  const activeIsPlatform = !!user?.active?.is_platform
  const onPlatformRoute = location.pathname.startsWith('/platform')

  // Bounce platform-active sessions to /platform/tenants whenever they
  // land anywhere else (deep-link, "back" button, stale bookmark). The
  // inverse case (tenant-active user hits /platform/*) is guarded by
  // <RequirePlatformAdmin> on those routes directly.
  if (activeIsPlatform && !onPlatformRoute) {
    return <Navigate to="/platform/tenants" replace />
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <Sidebar
        mobileOpen={mobileNavOpen}
        onMobileClose={() => setMobileNavOpen(false)}
      />

      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <TopBar
          wsStatus={wsStatus}
          onMobileNavToggle={() => setMobileNavOpen(v => !v)}
        />

        <ToastStack lastMessage={lastMessage} />

        {/* Content region — pages own their inner padding; the shell only
            provides the vertical scroll surface. */}
        <Box sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  )
}
